import { empty } from '@sdk/helpers/misc/nullable';
import { depatternize } from '@sdk/helpers/formatters/patternize';

import {
  ballooned,
  balloons,
  values,
  installment as installmentFinder,
  frequent,
} from '@sdk/views/Simulations/Read/installments';

const CUSTOM_COMMERCIAL_PROPERTIES = [
  'return_preference',
  'min_return',
  'max_return',
  'quota_preference',
  'credit_condition_plus',
  'include_financial_protection_insurance',
];

const CUSTOM_PRODUCT_OPTIONS_PROPERTIES = [
  'include_capitalization_bond',
  'include_asset_insurance',
  'include_personal_insurance',
];

export function bank(condition) {
  return condition.bank.febraban_code;
}

function fuel(simulation) {
  let type = {};

  try {
    type = simulation.vehicle.fuel_type;
  } catch (err) { /* */ }

  if (!type || !type.credere_identifier) {
    try {
      type = simulation.vehicle.vehicle_model.fuel_type;
    } catch (err) { /* */ }
  }

  return type.credere_identifier;
}

function installment(condition, simulation, down) {
  const upfront = (empty(down) ? condition.down_payment : down) || 0;

  const json = {
    down_payment: upfront,
    financed_amount: simulation.assets_value - upfront,
    installments: condition.installments,
    products_options: {},
  };

  if (condition.bank && condition.bank.febraban_code) {
    json.bank_febraban_code = condition.bank.febraban_code;
  }

  CUSTOM_COMMERCIAL_PROPERTIES
    .filter(property => !empty(condition[property]))
    .forEach((property) => {
      json[property] = condition[property];
    });

  if (condition.products_options) {
    CUSTOM_PRODUCT_OPTIONS_PROPERTIES
      .filter(property => !empty(condition.products_options[property]))
      .forEach((property) => {
        json.products_options[property] = condition.products_options[property];
      });
  }

  return json;
}

function installments(simulation, down) {
  return simulation.conditions
    .filter(condition => !condition.suggestion_reason)
    .map(condition => installment(condition, simulation, down));
}

export function detail(condition) {
  const vs = values(condition);
  const fq = frequent(vs, condition);
  const bn = ballooned(condition);
  const bs = bn ? balloons(vs, installmentFinder(bn, fq)) : {};

  return [
    condition.down_payment,
    fq.installments,
    fq.value,
    Object.keys(bs).map(b => `${b}c-c${bs[b].join('d-d')}`).join('b-b'),
    bank(condition),
  ].join('a-a');
}

export function link(simulation, conditions, queried, user) {
  const {
    proposal_id: proposal,
    proposal_attempt_id: attempt,
    seller_id: seller,
    store_id: store,
    commercial,
  } = queried;

  const sorted = conditions.sort((a, b) => a.id - b.id);

  if (!simulation.lead) {
    return {
      name: 'customers-new',
      query: {},
    };
  }

  const query = {
    store_id: store,
    seller_id: seller || (simulation.payload && simulation.payload.user_id) || user.id,
    cpf_cnpj: depatternize(simulation.lead.cpf_cnpj),
    simulation_uuid: simulation.uuid,
    condition_id: sorted.map(condition => condition.id).join(','),
    condition_detail: sorted.map(detail).join(','),
    bank_code: sorted.map(bank).join(','),
  };

  if (proposal) {
    query.proposal_id = proposal;
  }

  if (attempt) {
    query.proposal_attempt_id = attempt;
  }

  if (commercial) {
    query.commercial = true;
  }

  return {
    name: 'customers-new',
    query,
  };
}

export default function payload(simulation, down) {
  const storeID = simulation.payload ? simulation.payload.store_id : undefined;
  const leadCPF = simulation.lead ? simulation.lead.cpf_cnpj : undefined;
  const value = simulation.assets_value;
  const documentation = (simulation.items || [])
    .reduce((v, i) => v + (i.type === 'documentation' ? i.value : 0), 0);
  const accessory = (simulation.items || [])
    .reduce((v, i) => v + (i.type === 'accessory' ? i.value : 0), 0);

  const json = {
    header_store_id: storeID,
    cpf_cnpj: leadCPF,

    simulation: {
      process_bank_suggested_conditions: true,

      payload: {
        store_id: storeID,
        origin: 'pwa',
      },

      seller_cpf: simulation.seller_cpf || (simulation.seller && simulation.seller.cpf),
      retrieve_lead: { cpf_cnpj: leadCPF },

      assets_value: value,
      accessory_value: accessory,
      documentation_value: documentation,

      vehicle: {
        vehicle_molicar_code: simulation.vehicle.vehicle_model.molicar_code,
        licensing_uf: simulation.vehicle.licensing_uf,
        licensing_city: simulation.vehicle.licensing_city,
        manufacture_year: simulation.vehicle.manufacture_year,
        model_year: simulation.vehicle.model_year,
        retrieve_fuel_type: fuel(simulation),
        asset_value: value,
        zero_km: simulation.vehicle.zero_km,
      },

      conditions: installments(simulation, down),
    },
  };

  if (simulation.bank_febraban_codes) {
    json.bank_febraban_codes = simulation.bank_febraban_codes;
  }

  return json;
}
